import type { MachineSupaDto, OrganizationDto } from "@strtmn/sds/types"
import { getErrorMessage } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useEffect, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { getById } from "../../api/MachinesApi"
import { listOrgs, sortOrgs } from "../../api/OrgsApi"
import { ErrorBanner, LoadingErrorState, RoundedCard } from "../../components"
import MachineMask from "../../components/admin/MachineMask"
import { isProd } from "../../config"

export function AdminMachineCreateEditForm() {
  const location = useLocation()
  const [isNewMachine, setIsNewMachine] = useState<boolean>(true)
  const [machine, setMachine] = useState<MachineSupaDto | null>(null)
  const [allOrgs, setAllOrgs] = useState<OrganizationDto[] | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | undefined>(undefined)
  const params = useParams<{
    machineId: string | undefined
  }>()

  useEffect(() => {
    const loadAllOrgs = async () => {
      !isProd && console.debug("cem: > loadAllOrgs")
      setLoading(true)
      setAllOrgs(null)
      try {
        const _allOrgs = await listOrgs()
        if (!_allOrgs) {
          throw new Error("Could not load all orgs")
        }
        setAllOrgs(_allOrgs)
      } catch (err) {
        console.log(
          "cem.loadAllOrgs: Caught an error:",
          JSON.stringify(err, null, 2)
        )
        setError(getErrorMessage(err))
      }
    }
    loadAllOrgs()
  }, [])

  useEffect(() => {
    const loadMachine = async (machineId: string) => {
      !isProd && console.debug("cem: > loadMachine")
      setLoading(true)
      setMachine(null)
      try {
        const _machine = await getById(machineId)
        if (!_machine) {
          throw new Error("Could not load machine")
        }
        setMachine(_machine)
        setIsNewMachine(false)
      } catch (err) {
        console.log(
          "cem.loadMachine: Caught an error:",
          JSON.stringify(err, null, 2)
        )
        setError(getErrorMessage(err))
      }
    }

    if (location.state?.isNewMachine && location.state.machine) {
      !isProd && console.debug("Heck, this is a new machine:", location.state)
      setMachine(location.state.machine)
      setIsNewMachine(true)
    } else if (params.machineId) {
      !isProd && console.debug("machineId:", params.machineId)
      if (!isProd) {
        if ("new" === params.machineId) {
          console.debug("Heck, this is a new machine.")
        } else {
          console.debug("machineId:", params.machineId)
        }
      }
      if ("new" === params.machineId) {
        console.log("Creating new machines is not supported.")
        setError("Creating new machines is not supported.")
      } else {
        loadMachine(params.machineId)
      }
    } else {
      console.error("No machineId parameter")
    }
  }, [location.state, params.machineId])

  useEffect(() => {
    if (error) {
      !isProd && console.debug("⚡ Done loading, got an error:", error)
      setLoading(false)
    } else if (allOrgs && machine) {
      !isProd && console.debug("✅ Done loading, got machine and allOrgs")
      setLoading(false)
    } else {
      !isProd &&
        console.debug(
          "⏳ Not done loading. machine =",
          machine,
          " ; allOrgs =",
          allOrgs
        )
    }
  }, [allOrgs, error, machine])

  if (loading || error) {
    return (
      <LoadingErrorState
        isLoading={loading}
        loadingMessage={I18n.get("Lade Maschinendaten, bitte warten...")}
        isError={!!error}
        errorMessage={`${I18n.get("Fehler")}: ${I18n.get(error!)}`}
      />
    )
  }

  if (!machine) {
    return (
      <ErrorBanner error={I18n.get("Maschine erstellen fehlgeschlagen!")} />
    )
  } else {
    const loadedMachine = machine!
    const loadedAllOrgs = sortOrgs(allOrgs!)
    return (
      <main className="p-8 pt-2">
        <div className="page-heading mb-4">
          {I18n.get(isNewMachine ? "Maschine anlegen" : "Maschine bearbeiten")}
        </div>

        <RoundedCard>
          <MachineMask
            machine={loadedMachine}
            isNewMachine={isNewMachine}
            allOrgs={loadedAllOrgs}
          />
        </RoundedCard>
      </main>
    )
  }
}
